import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, Crosshair, Box, FileCode2 } from "lucide-react";
import { Page, Reveal, SectionTag } from "@/components/Reveal";
import { IMAGES, CONTACT_EMAIL, CONTACT_PHONE } from "@/lib/data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EQUIPMENT = [
  {
    icon: Crosshair,
    title: "Precision field measurement",
    body: "Laser distance measurement taken on site, point by point — never from old plans or secondhand photos.",
  },
  {
    icon: Box,
    title: "Matterport 3D scanning",
    body: "Complete digital twins of your space, walkable from any device and measurable inside the model.",
  },
  {
    icon: FileCode2,
    title: "CAD drafting standards",
    body: "Clean, layered DWG drawings and print-ready PDFs your architect can build from immediately.",
  },
];

const FAQ = [
  {
    q: "How fast do I get my drawings?",
    a: "DWG and PDF drawings arrive within 7 to 10 days of the site visit. Matterport tours are shared by link shortly after the scan.",
  },
  {
    q: "Do you travel to my state?",
    a: "Yes. We cover all 50 states and have surveyed spaces as far away as Puerto Rico and Guam. Travel is built into every quote.",
  },
  {
    q: "What do you need for site access?",
    a: "We coordinate access with you, the landlord, or the property manager. If a certificate of insurance is required, we can provide one on request.",
  },
  {
    q: "What formats do you deliver?",
    a: "As-built drawings in DWG and PDF, plus a shareable Matterport tour link when a 3D scan is part of the scope.",
  },
  {
    q: "How quickly will I get a quote?",
    a: "Typically within one business day of your request, with pricing and available site-visit dates.",
  },
];

export default function About() {
  return (
    <Page>
      <section className="blueprint-grid relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-36 sm:px-6 lg:px-8 lg:pt-44">
          <Reveal>
            <SectionTag>About</SectionTag>
            <h1
              data-testid="about-headline"
              className="max-w-4xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              Measured in person, wherever you need us
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
                Global Dimensions, Inc. provides as-built surveys and Matterport 3D scans
                for retail clients across the United States and beyond. Our job is simple:
                give your design and construction teams an accurate starting point for their
                next space.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                We measure every site in person. That means the dimensions in your drawings
                came from someone standing in the space with the right tools — not from old
                plans, estimates, or secondhand photos. It's the difference between a
                build-out that goes smoothly and one that's full of surprises.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                We travel wherever the work is. We cover all 50 states and have surveyed
                spaces as far away as Puerto Rico and Guam. If your next location is
                somewhere others won't go, we will.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <div className="relative">
                <span className="absolute -left-3 -top-3 h-6 w-6 border-l-2 border-t-2 border-cad/70" />
                <span className="absolute -bottom-3 -right-3 h-6 w-6 border-b-2 border-r-2 border-cad/70" />
                <div className="overflow-hidden rounded-sm border border-white/10">
                  <img
                    src={IMAGES.storefront}
                    alt="Field measuring at a retail storefront"
                    className="h-80 w-full object-cover lg:h-[420px]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-canvas/70 to-transparent" />
                </div>
              </div>
              <div
                data-testid="about-contact-card"
                className="mt-10 rounded-md border border-white/10 bg-surface p-7"
              >
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-cad/80">Direct contact</p>
                <a
                  data-testid="about-email-link"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-4 flex items-center gap-3 text-sm text-slate-200 transition-colors hover:text-cad"
                >
                  <Mail size={16} className="text-laser" /> {CONTACT_EMAIL}
                </a>
                <p className="mt-3 flex items-center gap-3 text-sm text-slate-400">
                  <Phone size={16} className="text-laser" /> {CONTACT_PHONE}
                </p>
                <Link
                  to="/quote"
                  data-testid="about-quote-button"
                  className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cad transition-colors hover:text-slate-100"
                >
                  Request a Quote
                  <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionTag>The person on site</SectionTag>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                One surveyor. One standard.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                Wesley Johnson has spent more than 25 years in the field providing as-built
                surveys — much of it for long-term clients who come back project after
                project. Global Dimensions is built on that kind of trust: careful
                measurement, honest drawings, and no surprises.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                With a bachelor's degree in architecture and a strong focus on retail,
                hotels, and office buildings, he reads a space the way your design team
                will — which is why the drawings land ready to build from. No handoffs and
                no subcontracted guesswork: the person who walks your space is the person
                accountable for the drawings.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <div
                data-testid="about-bio-card"
                className="blueprint-grid relative h-full rounded-md border border-white/10 bg-surface p-8 sm:p-10"
              >
                <span className="absolute -left-px -top-px h-6 w-6 border-l-2 border-t-2 border-cad/70" />
                <span className="absolute -bottom-px -right-px h-6 w-6 border-b-2 border-r-2 border-cad/70" />
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cad/80">
                  Principal · Field Surveyor
                </p>
                <p className="mt-3 font-display text-2xl font-bold text-slate-50 sm:text-3xl">
                  Wesley Johnson
                </p>
                <a
                  data-testid="about-bio-email-link"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-2 inline-block text-sm text-slate-300 transition-colors hover:text-cad"
                >
                  {CONTACT_EMAIL}
                </a>
                <div className="mt-8 flex flex-wrap gap-2">
                  {[
                    "25+ years in the field",
                    "Bachelor's in Architecture",
                    "Every site measured in person",
                    "All 50 states · PR · Guam",
                    "Retail · Hotels · Office",
                    "DWG / PDF / 3D deliverables",
                  ].map((chip) => (
                    <span
                      key={chip}
                      className="rounded-sm border border-white/10 bg-canvas px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                <p className="mt-9 border-l-2 border-laser/60 pl-5 font-display text-lg font-medium italic leading-relaxed text-slate-200">
                  "If it's in the drawing, it was verified on site."
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <Reveal>
            <SectionTag>In the field</SectionTag>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
              The right tools, on every site
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {EQUIPMENT.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.1}>
                <div className="h-full rounded-md border border-white/10 bg-canvas p-7 transition-colors duration-300 hover:border-cad/40">
                  <e.icon size={22} className="text-laser" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-100">{e.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-24 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag>FAQ</SectionTag>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
            Straight answers
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <Accordion type="single" collapsible className="mt-10" data-testid="about-faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-display text-base font-medium text-slate-200 hover:text-cad hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </Page>
  );
}
